import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  ClipboardList,
  Eye,
  Download,
  RefreshCw
} from 'lucide-react';
import API_BASE_URL from '@/config';

export const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPatientAndRelated = async () => {
    try {
      setLoading(true);
      const [pResp, prResp, rResp, fResp] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/patients/${id}`),
        axios.get(`${API_BASE_URL}/api/prescriptions`),
        axios.get(`${API_BASE_URL}/api/reports`),
        axios.get(`${API_BASE_URL}/api/followups`)
      ]);

        const patientData = pResp.data;
        setPatient(patientData);

        // Normalize id to string
        const pid = patientData.id || id;

        // Filter prescriptions/reports/followups for this patient
        const prescriptionsList = prResp.data?.value || prResp.data || [];
        const patientPrescriptions = prescriptionsList.filter(p => {
          const pidField = p.patient_id || p.patientId || p.patient || p.patientName || p.patient;
          return pidField == pid || p.patient_name == patientData.name || p.patientName == patientData.name;
        });
        setPrescriptions(patientPrescriptions);

        const reportsList = rResp.data?.value || rResp.data || [];
        const patientReports = reportsList.filter(r => {
          const pidField = r.patient_id || r.patientId || r.patient;
          return pidField == pid || r.patient_name == patientData.name || r.patientName == patientData.name;
        });
        setReports(patientReports);

        const followupsList = fResp.data?.value || fResp.data || [];
        const patientFollowups = followupsList.filter(f => {
          const pidField = f.patient_id || f.patientId || f.patient;
          return pidField == pid || f.patient_name == patientData.name || f.patientName == patientData.name;
        });

        // Map followups + prescriptions + reports to visit-like objects and sort by date desc
        const visitFromFollowups = patientFollowups.map(v => ({ id: v.id, date: v.date, notes: v.notes || v.condition || v.reason || '', condition: v.notes || 'Follow-up', source: 'followup' }));
        const visitFromPrescriptions = patientPrescriptions.map(p => ({ id: p.id, date: p.visit_date || p.date || '', notes: p.notes || '', condition: 'Prescription', source: 'prescription' }));
        const visitFromReports = patientReports.map(r => ({ id: r.id, date: r.visit_date || r.visitDate || r.date || '', notes: '', condition: r.report_name || r.reportName || r.name || 'Report', source: 'report' }));

        const allVisits = [...visitFromFollowups, ...visitFromPrescriptions, ...visitFromReports].filter(v => v.date).sort((a,b) => new Date(b.date) - new Date(a.date));
        setVisits(allVisits);


      } catch (error) {
        console.error('Error fetching patient details or related records:', error);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchPatientAndRelated();
    }, [id]);

    if (!patient) {
      return <div>Loading...</div>;
    }

    return (
      <div className="space-y-6">
        {/* Ensure patient object is defined */}
        {patient && (
          <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center justify-between w-full sm:w-auto">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Button variant="ghost" size="icon" className="h-10 w-10 flex-shrink-0" onClick={() => navigate('/admin/patients')}>
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div>
                    <h1 className="font-serif text-2xl sm:text-3xl font-bold">{patient.name}</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {patient.age} years • {patient.gender} {"•"} {(patient.lastVisit || patient.last_visit) ? new Date(patient.lastVisit || patient.last_visit).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
                {/* Mobile Icon-only Refresh Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchPatientAndRelated}
                  disabled={loading}
                  className="sm:hidden h-9 w-9 flex-shrink-0 ml-2"
                  aria-label="Refresh patient details"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                {/* Desktop Refresh Button */}
                <Button
                  variant="outline"
                  onClick={fetchPatientAndRelated}
                  disabled={loading}
                  className="hidden sm:inline-flex h-11 gap-1.5"
                  aria-label="Refresh patient details"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button asChild className="w-full sm:w-auto h-11">
                  <Link to={`/admin/patients/edit/${id}`}>Edit Details</Link>
                </Button>
              </div>
            </div>

          {/* Patient Info Card */}
          <Card className="shadow-card">
            <CardHeader className="border-b border-border py-4 px-4 sm:px-6">
              <CardTitle className="font-serif text-lg sm:text-xl">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2.5 rounded-lg flex-shrink-0">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Phone</p>
                    {patient.phone ? (
                      <a href={`tel:${patient.phone}`} className="font-medium text-primary hover:underline">
                        {patient.phone}
                      </a>
                    ) : (
                      <p className="font-medium">—</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2.5 rounded-lg flex-shrink-0">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                    <p className="font-medium truncate">{patient.email || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2.5 rounded-lg flex-shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Address</p>
                    <p className="font-medium">{patient.address || '—'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for different sections */}
          <Tabs defaultValue="visits" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="visits">Visit History</TabsTrigger>
              <TabsTrigger value="prescriptions">Prescriptions</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
            </TabsList>

            {/* Visit History */}
            <TabsContent value="visits">
              <Card className="shadow-card">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-serif text-xl">Visit History</CardTitle>
                    <Button size="sm" asChild>
                      <Link to="/admin/prescriptions/new">Add Visit</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {(visits || []).length > 0 ? (
                      visits.map((visit) => (
                        <div key={visit.id} className="p-6 hover:bg-muted/50 transition-colors">
                          <div className="flex items-start gap-4">
                            <div className="bg-primary/10 p-2 rounded-lg">
                              <Calendar className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-lg">{visit.condition}</p>
                                  <p className="text-sm text-muted-foreground">{visit.date}</p>
                                </div>                                <div className="flex gap-2">
                                  {visit.source === 'prescription' && (
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/prescriptions/${visit.id}`)}>View</Button>
                                  )}
                                  {visit.source === 'report' && (
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/reports/${visit.id}`)}>View</Button>
                                  )}
                                </div>                              </div>
                              <p className="text-muted-foreground">{visit.notes}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-muted-foreground">
                        No visit history available.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Prescriptions */}
            <TabsContent value="prescriptions">
              <Card className="shadow-card">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-serif text-xl">Prescriptions</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {(prescriptions || []).length > 0 ? (
                      prescriptions.map((prescription) => (
                        <div key={prescription.id} className="p-6 hover:bg-muted/50 transition-colors">
                          <div className="flex items-start gap-4">
                            <div className="bg-accent/10 p-2 rounded-lg">
                              <FileText className="h-5 w-5 text-accent" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold">Prescription #{prescription.id}</p>
                                    <Badge variant="secondary">{prescription.type}</Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{prescription.date || prescription.visit_date || ''}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/prescriptions/${prescription.id}`)}>View</Button>
                                  {prescription.file_url && (
                                    <Button size="sm" variant="ghost" asChild>
                                      <a href={prescription.file_url} target="_blank" rel="noreferrer">Open</a>
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <p className="text-muted-foreground">
                                <span className="font-medium">Medicines:</span> {Array.isArray(prescription.medicines) ? prescription.medicines.map(m => typeof m === 'string' ? m : m.name).join(', ') : prescription.medicines}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-muted-foreground">
                        No prescriptions available.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports */}
            <TabsContent value="reports">
              <Card className="shadow-card">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-serif text-xl">Medical Reports</CardTitle>
                    <Button size="sm" asChild>
                      <Link to="/admin/reports">Upload Report</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {(reports || []).length > 0 ? (
                      reports.map((report) => (
                        <div key={report.id} className="p-6 hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="bg-primary/10 p-2 rounded-lg">
                            <ClipboardList className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold">{report.report_name || report.reportName || report.name || 'Report'}</p>
                            <p className="text-sm text-muted-foreground">{report.date || report.visit_date || report.visitDate || ''}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => report.file_url ? window.open(report.file_url, '_blank') : navigate(`/admin/reports/${report.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => report.file_url ? window.open(report.file_url, '_blank') : navigate(`/admin/reports/${report.id}`)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-muted-foreground">
                        No reports available.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default PatientDetail;
